import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "../../lib/cn";
import { focusRing } from "../../lib/focusRing";
import { Spinner } from "../Spinner/Spinner";

/*
 * Three colours plus ghost. `secondary` and `danger` render through container
 * pairs so they read as tonal fills, and every variant is theme driven: no
 * literal colour appears here.
 */
export const buttonVariants = cva(
  cn(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-control font-display font-semibold tracking-wide whitespace-nowrap transition-colors duration-200 select-none",
    "disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress",
    focusRing,
  ),
  {
    variants: {
      variant: {
        primary: "bg-accent-500 text-on-accent shadow-sm hover:bg-accent-400 active:bg-accent-600",
        secondary:
          "bg-accent-container text-on-accent-container hover:bg-accent-100 dark:hover:bg-accent-600",
        danger: "bg-danger-container text-on-danger-container hover:brightness-95",
        ghost: "text-subtle hover:bg-overlay hover:text-heading",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-5 text-sm",
        lg: "h-12 px-7 text-base",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button>, merging props onto it (e.g. a router Link). */
  asChild?: boolean;
  /** Shows a spinner and blocks interaction. */
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (asChild) {
    return (
      <Slot className={classes} aria-busy={loading || undefined} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner size="sm" label="Working" className="text-current" /> : null}
      {children}
    </button>
  );
}
